import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Box } from '@mui/material';

interface SafeHtmlProps {
  html: string;
}

// Allow only the formatting tags the polish prompt is instructed to emit.
// Exported so other HTML producers/consumers (e.g. RichTextEditor) sanitize
// against the exact same allow-list as this renderer.
export const ALLOWED_TAGS = [
  'p', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'h4', 'h5', 'br', 'code', 'pre',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a', 'span', 'b', 'i',
];
export const ALLOWED_ATTR = ['href', 'target', 'rel'];

/**
 * Render model-generated HTML safely. Strips scripts, event handlers, styles,
 * and any tag/attribute outside the allow-list. Links open in a new tab.
 */
const SafeHtml: React.FC<SafeHtmlProps> = ({ html }) => {
  const clean = useMemo(() => {
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
    });
    // Force safe link behavior.
    return sanitized.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  }, [html]);

  return (
    <Box
      sx={{
        fontSize: '0.95rem',
        lineHeight: 1.8,
        wordBreak: 'break-word',
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        '& p': { my: 0.75 },
        '& ul, & ol': { my: 0.75, pl: 3 },
        '& li': { mb: 0.5 },
        '& h4': { fontSize: '1.15rem', fontWeight: 700, my: 1 },
        '& h5': { fontSize: '1.02rem', fontWeight: 600, my: 0.75 },
        '& strong': { fontWeight: 700 },
        '& code': {
          bgcolor: 'action.hover',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
          fontSize: '0.85em',
          fontFamily: 'monospace',
        },
        '& pre': {
          bgcolor: 'action.hover',
          p: 1.25,
          borderRadius: 1.5,
          overflowX: 'auto',
          my: 1,
          '& code': { bgcolor: 'transparent', px: 0, py: 0 },
        },
        '& a': { color: 'primary.main', textDecoration: 'underline' },
        '& table': { borderCollapse: 'collapse', width: '100%', my: 1.25, fontSize: '0.9rem', display: 'block', overflowX: 'auto' },
        '& th, & td': { border: 1, borderColor: 'divider', px: 1.25, py: 0.75, textAlign: 'inherit' },
        '& th': { bgcolor: 'action.hover', fontWeight: 700 },
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

export default SafeHtml;
